// Generic search helper for MUI DataGrid tables.
//
// Filters rows by matching the search term against the value of every
// *data* column defined for the grid, so search covers all visible columns
// automatically (and keeps working when columns are added or changed).
//
// Non-data columns (actions, and anything explicitly excluded) are skipped.

// Fields that are never useful to search against (buttons, images, toggles).
const DEFAULT_EXCLUDED_FIELDS = new Set(['actions', 'action', 'logo']);

/**
 * Resolve the searchable text for a single column of a row.
 * Prefers the column's valueGetter (so derived/labelled values are searched),
 * falling back to the raw field value.
 */
const getColumnText = (column, row) => {
  const { field, valueGetter } = column;
  let value = row?.[field];

  if (typeof valueGetter === 'function') {
    try {
      // MUI v8 signature: valueGetter(value, row, column, apiRef)
      value = valueGetter(row?.[field], row, column);
    } catch {
      value = row?.[field];
    }
  }

  if (value === null || value === undefined) return '';
  return String(value);
};

/**
 * Filter grid rows against a search term across all data columns.
 *
 * @param {Array} rows - the full row set
 * @param {string} searchTerm - the current search text
 * @param {Array} columns - the DataGrid column definitions
 * @param {Object} [options]
 * @param {string[]} [options.excludeFields] - extra field names to skip
 * @returns {Array} filtered rows
 */
export const filterRowsByColumns = (rows, searchTerm, columns, options = {}) => {
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) return rows;

  const excluded = new Set([
    ...DEFAULT_EXCLUDED_FIELDS,
    ...(options.excludeFields || []),
  ]);

  const searchableColumns = (columns || []).filter(
    (col) => col && col.field && !excluded.has(col.field)
  );

  return rows.filter((row) =>
    searchableColumns.some((col) =>
      getColumnText(col, row).toLowerCase().includes(term)
    )
  );
};
